import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { ASINReviewFormData } from '../types/asinReview';
import { PMT3WriteAPIResponse } from '../types/pmt3Types';

const pmt3WriteFn = httpsCallable<
  { operation: string; data: any },
  PMT3WriteAPIResponse
>(functions, 'pmt3Write');

/**
 * PMT3 Write Service - Client-side wrapper for Cloud Function
 */
class PMT3WriteService {
  private async callFunction(operation: string, data: any): Promise<PMT3WriteAPIResponse> {
    try {
      const result = await pmt3WriteFn({ operation, data });
      return result.data;
    } catch (error: any) {
      console.error(`PMT3 API call failed for operation ${operation}:`, error);
      throw error;
    }
  }

  async completeASINReview(
    formData: ASINReviewFormData,
    spreadsheetId: string,
    currentASIN?: string
  ): Promise<PMT3WriteAPIResponse> {
    return this.callFunction('completeASINReview', { formData, spreadsheetId, currentASIN });
  }

  async pasteClientNotes(notes: string, spreadsheetId: string): Promise<PMT3WriteAPIResponse> {
    return this.callFunction('pasteClientNotes', { notes, spreadsheetId });
  }

  async nextASIN(spreadsheetId: string, currentASIN?: string): Promise<PMT3WriteAPIResponse> {
    return this.callFunction('nextASIN', { spreadsheetId, currentASIN });
  }

  async validateForm(formData: ASINReviewFormData): Promise<PMT3WriteAPIResponse> {
    return this.callFunction('validateForm', { formData });
  }

  validateFormClientSide(formData: ASINReviewFormData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!formData.newGPO.trim() && !formData.newTargetACoS?.trim() && !formData.newMaxBid?.trim()) errors.push('New GPO, Target ACoS, or Max Bid is required');
    if (formData.newPrice === null || formData.newPrice <= 0) errors.push('New Price must be a positive number');
    if (!formData.ppcStatus.trim()) errors.push('PPC Status is required');
    if (!formData.asinReviewCadence.trim()) errors.push('ASIN Review Cadence is required');

    const hasLedgerEntry = formData.ledgerEntries.some(entry => entry.trim() !== '');
    if (!hasLedgerEntry) errors.push('At least one Ledger Entry is required');

    const hasSituationEntry = formData.overallSituation.some(entry => entry.trim() !== '');
    if (!hasSituationEntry) errors.push('At least one Overall Situation entry is required');

    return { isValid: errors.length === 0, errors };
  }
}

export const pmt3WriteService = new PMT3WriteService();
export default pmt3WriteService;
